// Sözleşmeler — PDF yükle, imzaya gönder, müşteri parmak/fare ile imzalar,
// imza + denetim izi sayfası PDF'e eklenir. (5070 s.k. anlamında güvenli e-imza değildir.)
import { api } from '../api';
import { state, isAdmin, navigate } from '../app';
import { esc, $, $$, setHTML, formatDate, statusBadge, icons } from '../helpers';
import { SignPad } from '../signpad';
import { renderPdf } from '../pdfview';
import { openSheet, closeSheet, refreshContractGate } from '../shell';
import { toast, openModal, closeModal, confirmDialog, setBreadcrumb, skeletonTable } from '../ui';

declare const PDFLib: any;

const CONTRACT_STATUS: Record<string, string> = { draft: 'Taslak', sent: 'İmza bekliyor', signed: 'İmzalandı', cancelled: 'İptal' };
const STATUS_CLS: Record<string, string> = { draft: 'draft', sent: 'pending', signed: 'paid', cancelled: 'cancelled' };
const badge = (s: string) => `<span class="status status-${STATUS_CLS[s] || 'draft'}"><span class="status-dot"></span>${CONTRACT_STATUS[s] || s}</span>`;

function fileToBase64(file: File): Promise<string> {
  return new Promise((res, rej) => { const r = new FileReader(); r.onload = () => res((r.result as string).split(',')[1]); r.onerror = rej; r.readAsDataURL(file); });
}

async function authedBlob(path: string): Promise<Blob> {
  const res = await fetch(path, { headers: { Authorization: 'Bearer ' + state.token } });
  if (!res.ok) { const d: any = await res.json().catch(() => ({})); throw new Error(d.error || 'Dosya alınamadı'); }
  return res.blob();
}

export async function openContractFile(id: number, signed = false) {
  // Yeni sekme yerine uygulama içinde: sözleşme portaldan çıkmadan okunur.
  const sheet = openSheet(signed ? 'İmzalı nüsha' : 'Sözleşme', '<div id="pdf-host"></div>');
  const host = sheet.querySelector('#pdf-host') as HTMLElement;
  host.innerHTML = '<div class="content-loading"><div class="spinner"></div></div>';
  try {
    const blob = await authedBlob('/api/contracts/' + id + '/file' + (signed ? '?signed=1' : ''));
    await renderPdf(host, new Uint8Array(await blob.arrayBuffer()));
  } catch (err: any) {
    host.textContent = '';
    toast(err.message, 'error');
    closeSheet();
  }
}

// ─── Liste ──────────────────────────────────────────────────────────────────
export async function renderContracts() {
  const content = $('#content')!;
  setBreadcrumb([{ label: 'Panel', href: '/dashboard' }, { label: 'Sözleşmeler' }]);
  setHTML(content, skeletonTable(5, 4));
  try {
    const data = await api.get('/contracts');
    const rows: any[] = data.contracts || [];
    const waiting = rows.filter(r => r.status === 'sent');
    const list = rows.map(r => `<div class="client-row" style="cursor:default">
      <div class="client-main">
        <div class="client-title">${esc(r.title)}</div>
        <div class="client-sub">${isAdmin() ? esc(r.client_name || '') + ' · ' : ''}${esc(r.file_name)}</div>
        <div class="client-meta">${r.status === 'signed' ? 'İmza: ' + esc(r.signer_name || '') + ' · ' + formatDate(r.signed_at) : (r.sent_at ? 'Gönderildi ' + formatDate(r.sent_at) : 'Taslak ' + formatDate(r.created_at))}</div>
        <div class="client-chips">${badge(r.status)}</div>
      </div>
      <div class="client-fin" style="gap:6px">
        ${r.status === 'sent' && !isAdmin() ? `<button class="btn btn-primary btn-sm" data-sign="${r.id}"><i data-lucide="pen-line"></i> İmzala</button>` : ''}
        <button class="btn btn-secondary btn-sm" data-open="${r.id}" data-signed="${r.has_signed ? '1' : '0'}"><i data-lucide="file-text"></i> ${r.has_signed ? 'İmzalı PDF' : 'PDF'}</button>
        ${isAdmin() && r.status !== 'signed' ? `<button class="btn btn-ghost btn-sm" data-del="${r.id}"><i data-lucide="trash-2"></i></button>` : ''}
      </div></div>`).join('');
    setHTML(content, `<div>
      <div class="page-header"><div><h1>Sözleşmeler</h1><p class="page-sub">${rows.length} sözleşme${waiting.length ? ' · ' + waiting.length + ' imza bekliyor' : ''}</p></div>
      ${isAdmin() ? '<div class="page-header-actions"><button class="btn btn-primary" id="new-contract"><i data-lucide="file-plus"></i> Sözleşme yükle</button></div>' : ''}</div>
      ${!isAdmin() && waiting.length ? `<div class="auth-notice" style="margin-bottom:12px"><strong>${waiting.length} sözleşme imzanızı bekliyor</strong>Belgeyi okuyup telefonunuzdan veya bilgisayarınızdan imzalayabilirsiniz.</div>` : ''}
      ${rows.length ? `<div class="client-list">${list}</div>` : '<div class="empty-state"><i data-lucide="file-signature"></i><p>Sözleşme yok</p></div>'}
    </div>`);
    icons();
    document.getElementById('new-contract')?.addEventListener('click', () => openContractUpload());
    $$('[data-open]').forEach(b => b.addEventListener('click', () => openContractFile(parseInt(b.getAttribute('data-open')!), b.getAttribute('data-signed') === '1')));
    $$('[data-sign]').forEach(b => b.addEventListener('click', () => navigate('/contracts/' + b.getAttribute('data-sign'))));
    $$('[data-del]').forEach(b => b.addEventListener('click', async () => {
      if (await confirmDialog('Sözleşmeyi sil', 'Bu sözleşme silinsin mi?', 'Sil', 'danger')) {
        try { await api.del('/contracts/' + b.getAttribute('data-del')); toast('Silindi', 'success'); renderContracts(); } catch (err: any) { toast(err.message, 'error'); }
      }
    }));
  } catch (err: any) { toast(err.message, 'error'); }
}

// ─── Yükleme (yönetici) ─────────────────────────────────────────────────────
export async function openContractUpload(clientId?: number) {
  let opts = '';
  try {
    const cd = await api.get('/clients');
    opts = (cd.clients || []).filter((c: any) => c.is_active).map((c: any) => `<option value="${c.id}"${clientId === c.id ? ' selected' : ''}>${esc(c.company || c.name)}</option>`).join('');
  } catch { /* yoksay */ }
  openModal('Sözleşme yükle',
    `<form id="contract-form" class="auth-form" novalidate>
      <div class="form-group"><label>Müşteri *</label><select name="client_id" required>${opts}</select></div>
      <div class="form-group"><label>Sözleşme başlığı *</label><input type="text" name="title" required placeholder="Örn. 2026 SEO Hizmet Sözleşmesi"></div>
      <div class="form-group"><label>PDF dosyası * <span class="muted">(en fazla 10MB)</span></label><input type="file" id="contract-file" accept="application/pdf,.pdf" required></div>
      <div class="form-group"><label>Not</label><input type="text" name="notes" placeholder="İç not, müşteri görmez"></div>
      <label class="check-row"><input type="checkbox" name="send" checked><span>Yükledikten sonra müşteriye imza için gönder</span></label>
      <div class="modal-footer" style="padding:1rem 0 0"><button type="button" class="btn btn-secondary" id="ct-cancel">Vazgeç</button><button type="submit" class="btn btn-primary">Yükle</button></div>
    </form>`);
  document.getElementById('ct-cancel')?.addEventListener('click', closeModal);
  document.getElementById('contract-form')?.addEventListener('submit', async (e) => {
    e.preventDefault();
    const file = (document.getElementById('contract-file') as HTMLInputElement).files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.pdf')) { toast('Sadece PDF yüklenebilir', 'error'); return; }
    if (file.size > 10 * 1024 * 1024) { toast('Dosya çok büyük (en fazla 10MB)', 'error'); return; }
    const fd = new FormData(e.target as HTMLFormElement);
    const body: any = Object.fromEntries(fd);
    body.send = fd.get('send') === 'on';
    body.client_id = parseInt(body.client_id);
    body.file_name = file.name;
    body.file_key = await fileToBase64(file);
    try { await api.post('/contracts', body); toast(body.send ? 'Sözleşme imzaya gönderildi' : 'Taslak kaydedildi', 'success'); closeModal(); navigate('/contracts'); }
    catch (err: any) { toast(err.message, 'error'); }
  });
}

// ─── İmza ekranı ────────────────────────────────────────────────────────────
export async function renderContractSign(id: number) {
  const content = $('#content')!;
  setBreadcrumb([{ label: 'Panel', href: '/dashboard' }, { label: 'Sözleşmeler', href: '/contracts' }, { label: 'Sözleşme' }]);
  setHTML(content, '<div class="content-loading"><div class="spinner"></div></div>');
  try {
    const { contract: ct } = await api.get('/contracts/' + id);
    const canSign = !isAdmin() && ct.status === 'sent';
    setBreadcrumb([{ label: 'Panel', href: '/dashboard' }, { label: 'Sözleşmeler', href: '/contracts' }, { label: ct.title }]);
    setHTML(content, `<div>
      <div class="page-header"><div><h1>${esc(ct.title)}</h1><p class="page-sub">${esc(ct.file_name)} · ${badge(ct.status)}</p></div>
        <div class="page-header-actions">
        ${canSign ? '' : '<button class="btn btn-secondary" id="open-pdf"><i data-lucide="external-link"></i> Sözleşmeyi aç</button>'}
        ${ct.has_signed ? '<button class="btn btn-secondary" id="open-signed"><i data-lucide="download"></i> İmzalı nüsha</button>' : ''}</div></div>

      <div class="card mb-2"><div class="card-body" style="padding:10px">
        <div id="pdf-host"><div class="content-loading"><div class="spinner"></div></div></div>
      </div></div>

      ${ct.status === 'signed' ? `<div class="card"><div class="card-header"><h3>Elektronik onay kaydı</h3><span class="chip chip-ok">İmzalandı</span></div><div class="card-body"><div class="kv">
          <div><span>İmzalayan</span><span>${esc(ct.signer_name || '')}</span></div>
          <div><span>Tarih</span><span>${formatDate(ct.signed_at)}</span></div>
          <div><span>IP adresi</span><span class="font-mono">${esc(ct.signer_ip || '—')}</span></div>
          <div><span>Belge özeti</span><span class="font-mono" style="word-break:break-all;font-size:.78rem">${esc(ct.file_hash || '')}</span></div>
        </div></div></div>` : ''}

      ${canSign ? `<div class="read-bar" id="read-bar">
        <p class="read-note" id="read-note">Sözleşmeyi sonuna kadar okuyun, imza adımı açılacak.</p>
        <button class="btn btn-primary btn-block" id="sign-cta" disabled><i data-lucide="pen-line"></i> <span id="sign-cta-label">İmzala</span></button>
      </div>` : ''}
    </div>`);
    icons();

    // PDF uygulama içinde çiziliyor (iOS'ta iframe PDF'i kaydırılamıyor).
    let pdfBytes: Uint8Array | null = null;
    let readToEnd = false;
    const unlockSigning = () => {
      readToEnd = true;
      const f = document.getElementById('sign-field') as HTMLButtonElement | null;
      const n = document.getElementById('read-note');
      if (f) f.disabled = false;
      if (n) n.remove();
      const c = document.getElementById('sign-cta') as HTMLButtonElement | null;
      if (c) c.disabled = false;
    };
    try {
      const blob = await authedBlob('/api/contracts/' + id + '/file' + (ct.has_signed ? '?signed=1' : ''));
      pdfBytes = new Uint8Array(await blob.arrayBuffer());
      await renderPdf(document.getElementById('pdf-host')!, pdfBytes, { onLastPageSeen: unlockSigning });
    } catch (err: any) { toast(err.message, 'error'); unlockSigning(); }

    document.getElementById('open-pdf')?.addEventListener('click', () => openContractFile(id, false));
    document.getElementById('open-signed')?.addEventListener('click', () => openContractFile(id, true));
    if (!canSign) return;

    // İmza akışı katmanlı: her adım ekrana sığar, kaydırma gerekmez.
    //   1) sayfa: yalnızca sözleşme + sabit alt çubuk
    //   2) katman: tam ekran imza yüzeyi
    //   3) katman: onay (imza önizleme, ad, "okudum" kutusu, gönder)
    const pad = new SignPad();
    let layer: HTMLElement | null = null;

    const closeLayer = () => { pad.detach(); layer?.remove(); layer = null; };

    const openFullPad = () => {
      closeLayer();
      const el = document.createElement('div');
      el.className = 'sign-full';
      el.innerHTML =
        '<div class="sign-full-head">'
        + '<button class="sheet-action" id="sf-cancel">Vazgeç</button>'
        + '<span class="sign-full-title">İmzanız</span>'
        + '<span></span></div>'
        + '<div class="sign-full-body">'
        + '<div class="sign-full-surface"><canvas id="sf-pad"></canvas><span class="sign-baseline"></span></div>'
        + '<p class="sign-full-tip">Parmağınızla imzalayın. Telefonu yatay tutarsanız daha rahat olur.</p>'
        + '</div>'
        + '<div class="sign-full-foot">'
        + '<button class="btn btn-secondary" id="sf-clear">Temizle</button>'
        + '<button class="btn btn-primary" id="sf-use">Devam</button></div>';
      document.body.appendChild(el);
      layer = el;
      pad.attach(el.querySelector('#sf-pad') as HTMLCanvasElement);
      pad.startHint();
      icons();

      el.querySelector('#sf-cancel')?.addEventListener('click', () => { pad.clear(); closeLayer(); });
      el.querySelector('#sf-clear')?.addEventListener('click', () => { pad.clear(); pad.startHint(); });
      el.querySelector('#sf-use')?.addEventListener('click', () => {
        if (pad.isEmpty) { toast('Önce imzanızı atın', 'error'); return; }
        openConfirmLayer();
      });
    };

    const openConfirmLayer = () => {
      const png = pad.toPNG();          // katman kapanmadan al
      closeLayer();
      const el = document.createElement('div');
      el.className = 'sign-full';
      el.innerHTML =
        '<div class="sign-full-head">'
        + '<button class="sheet-action" id="sc-back">Yeniden imzala</button>'
        + '<span class="sign-full-title">Onay</span>'
        + '<span></span></div>'
        + '<div class="sign-confirm">'
        + '<div class="sign-preview"><img alt="İmzanız" src="' + png + '"></div>'
        + '<div class="form-group"><label for="sc-name">Ad Soyad</label>'
        + '<input type="text" id="sc-name" autocomplete="name" placeholder="İmzalayan kişi" value="' + esc(state.user?.name || '') + '"></div>'
        + '<label class="check-row"><input type="checkbox" id="sc-consent">'
        + '<span>Sözleşmeyi okudum, kabul ediyorum ve elektronik olarak imzalıyorum.</span></label>'
        + '<p class="auth-privacy" style="text-align:left">İmzanız, tarih, saat, IP adresiniz ve belgenin dijital özeti kayda geçer. '
        + 'Bu onay, 5070 sayılı kanundaki güvenli elektronik imza değildir; taraflar arasında delil niteliğinde elektronik onaydır.</p>'
        + '</div>'
        + '<div class="sign-full-foot">'
        + '<button class="btn btn-primary" id="sc-submit" disabled>İmzala ve gönder</button></div>';
      document.body.appendChild(el);
      layer = el;
      icons();

      const consent = el.querySelector('#sc-consent') as HTMLInputElement;
      const submit = el.querySelector('#sc-submit') as HTMLButtonElement;
      consent.addEventListener('change', () => { submit.disabled = !consent.checked; });
      el.querySelector('#sc-back')?.addEventListener('click', () => { closeLayer(); openFullPad(); });
      submit.addEventListener('click', () => {
        const name = (el.querySelector('#sc-name') as HTMLInputElement).value.trim();
        if (!name) { toast('Ad soyad girin', 'error'); return; }
        submitSigned(name, png, submit);
      });
    };

    const submitSigned = async (name: string, sigPng: string, btn: HTMLButtonElement) => {
      if (!pdfBytes) { toast('Sözleşme yüklenemedi, sayfayı yenileyin', 'error'); return; }
      if (typeof PDFLib === 'undefined') { toast('PDF aracı yüklenemedi, bağlantınızı kontrol edin', 'error'); return; }
      btn.disabled = true; btn.textContent = 'İmzalanıyor…';
      try {
        const { PDFDocument, StandardFonts, rgb } = PDFLib;
        const doc = await PDFDocument.load(pdfBytes);
        const png = await doc.embedPng(sigPng);
        const font = await doc.embedFont(StandardFonts.Helvetica);
        const bold = await doc.embedFont(StandardFonts.HelveticaBold);
        const page = doc.addPage([595.28, 841.89]); // A4
        const M = 56; let y = 780;
        const line = (t: string, size = 10, f = font, color = rgb(0.12, 0.13, 0.16)) => { page.drawText(t, { x: M, y, size, font: f, color }); y -= size + 8; };
        line('ELEKTRONIK ONAY VE IMZA SAYFASI', 15, bold);
        y -= 6;
        page.drawLine({ start: { x: M, y: y + 8 }, end: { x: 595.28 - M, y: y + 8 }, thickness: 1, color: rgb(0.85, 0.85, 0.83) });
        y -= 10;
        const tr = (s: string) => (s || '').replace(/ı/g, 'i').replace(/İ/g, 'I').replace(/ş/g, 's').replace(/Ş/g, 'S').replace(/ğ/g, 'g').replace(/Ğ/g, 'G').replace(/ü/g, 'u').replace(/Ü/g, 'U').replace(/ö/g, 'o').replace(/Ö/g, 'O').replace(/ç/g, 'c').replace(/Ç/g, 'C');
        line('Sozlesme: ' + tr(ct.title));
        line('Dosya: ' + tr(ct.file_name));
        line('Imzalayan: ' + tr(name));
        line('Musteri: ' + tr(ct.client_company || ct.client_name || ''));
        line('Tarih (yerel): ' + new Date().toLocaleString('tr-TR'));
        line('Belge ozeti (SHA-256, imza oncesi): ' + (ct.file_hash || '').slice(0, 32));
        line((ct.file_hash || '').slice(32), 10);
        y -= 6;
        line('Imza:', 10, bold);
        const dims = png.scale(Math.min(1, 240 / png.width));
        page.drawImage(png, { x: M, y: y - dims.height, width: dims.width, height: dims.height });
        y -= dims.height + 10;
        page.drawLine({ start: { x: M, y }, end: { x: M + 260, y }, thickness: 1, color: rgb(0.6, 0.6, 0.6) });
        y -= 22;
        line(tr(name), 10, bold);
        y -= 10;
        const note = 'Bu sayfa, sozlesmenin ilgili kisi tarafindan elektronik ortamda onaylandigini gosterir. 5070 sayili';
        const note2 = 'Kanun anlamindaki guvenli elektronik imza degildir; taraflar arasinda delil niteligindedir.';
        page.drawText(note, { x: M, y, size: 8, font, color: rgb(0.45, 0.45, 0.48) }); y -= 12;
        page.drawText(note2, { x: M, y, size: 8, font, color: rgb(0.45, 0.45, 0.48) });
        const out = await doc.save();
        let bin = ''; const chunk = 0x8000;
        for (let i = 0; i < out.length; i += chunk) bin += String.fromCharCode.apply(null, Array.from(out.subarray(i, i + chunk)) as any);
        await api.post('/contracts/' + id + '/sign', { signer_name: name, signature_png: sigPng.split(',')[1], signed_file_key: btoa(bin) });
        closeLayer();
        toast('Sözleşme imzalandı', 'success');
        await refreshContractGate();   // imzalandı, kapı kalksın (başka bekleyen varsa yeniden çıkar)
        renderContractSign(id);
      } catch (err: any) {
        toast(err.message || 'İmzalama başarısız', 'error');
        btn.disabled = false; btn.textContent = 'İmzala ve gönder';
      }
    };

    // Sayfadaki tek eylem: imza katmanını açmak.
    const cta = document.getElementById('sign-cta') as HTMLButtonElement | null;
    cta?.addEventListener('click', openFullPad);
  } catch (err: any) { toast(err.message, 'error'); }
}
